import { useState } from "react";
import { submitQuote } from "@/lib/api";
import { ArrowRight, CheckCircle2, AlertCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pushLeadToDataLayer } from "@/lib/dataLayer";

const COURSE_OPTIONS = [
    "BLS-AED SRC (Aziende)",
    "Primo Soccorso completo (Aziende)",
    "Workshop personalizzato",
    "BLS-AED SRC (Privati)",
    "Corso Samaritani",
    "Defibrillatore — vendita / noleggio",
    "Altro",
];

export default function QuoteForm({ defaultCourse = "" }) {
    const [form, setForm] = useState({
        name: "", email: "", phone: "", company: "",
        course_interest: defaultCourse, participants: "",
        message: "",
    });
    const [status, setStatus] = useState({ kind: "idle", text: "" });

    const setField = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    async function onSubmit(e) {
        e.preventDefault();
        setStatus({ kind: "loading", text: "Invio in corso..." });
        try {
            const payload = {
                ...form,
                participants: form.participants ? Number(form.participants) : null,
            };
            const res = await submitQuote(payload);
            pushLeadToDataLayer({
                form_name: defaultCourse ? "quote_form_defibrillatore" : "quote_form_contatti",
                name: form.name,
                email: form.email,
                phone: form.phone,
                company: form.company,
                course: form.course_interest,
                participants: payload.participants,
                message: form.message,
            });
            setStatus({ kind: "success", text: res.message || "Richiesta inviata. Ti contatteremo entro 24 ore." });
            setForm({ name: "", email: "", phone: "", company: "", course_interest: defaultCourse, participants: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Errore durante l'invio. Riprova o scrivi a backoffice@7med.ch";
            setStatus({ kind: "error", text: typeof detail === "string" ? detail : "Errore. Riprova." });
        }
    }

    return (
        <form onSubmit={onSubmit} className="space-y-6" data-testid="quote-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <Field label="Nome e cognome" required>
                    <input
                        type="text"
                        required
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        className="swiss-input"
                        data-testid="quote-input-name"
                    />
                </Field>
                <Field label="Email" required>
                    <input
                        type="email"
                        required
                        value={form.email}
                        onChange={(e) => setField("email", e.target.value)}
                        className="swiss-input"
                        data-testid="quote-input-email"
                    />
                </Field>
                <Field label="Telefono">
                    <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setField("phone", e.target.value)}
                        className="swiss-input"
                        data-testid="quote-input-phone"
                    />
                </Field>
                <Field label="Azienda">
                    <input
                        type="text"
                        value={form.company}
                        onChange={(e) => setField("company", e.target.value)}
                        className="swiss-input"
                        data-testid="quote-input-company"
                    />
                </Field>
                <Field label="Corso di interesse">
                    <Select value={form.course_interest} onValueChange={(v) => setField("course_interest", v)}>
                        <SelectTrigger className="rounded-none border-0 border-b border-ink/30 bg-transparent px-0 h-12 focus:ring-0 focus:border-brand" data-testid="quote-select-course">
                            <SelectValue placeholder="Seleziona un corso" />
                        </SelectTrigger>
                        <SelectContent>
                            {COURSE_OPTIONS.map((c) => (
                                <SelectItem key={c} value={c}>{c}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </Field>
                <Field label="Numero partecipanti">
                    <input
                        type="number"
                        min="1"
                        value={form.participants}
                        onChange={(e) => setField("participants", e.target.value)}
                        className="swiss-input"
                        data-testid="quote-input-participants"
                    />
                </Field>
            </div>
            <Field label="Messaggio">
                <textarea
                    rows={4}
                    value={form.message}
                    onChange={(e) => setField("message", e.target.value)}
                    className="swiss-input resize-none"
                    placeholder="Raccontaci le tue esigenze..."
                    data-testid="quote-input-message"
                />
            </Field>

            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-4">
                <p className="text-xs text-ink-muted max-w-md">
                    Inviando questo form accetti la nostra <a href="/privacy-policy" className="underline hover:text-brand">Privacy Policy</a>. Risposta garantita entro 24 ore.
                </p>
                <button
                    type="submit"
                    disabled={status.kind === "loading"}
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    data-testid="quote-submit-button"
                >
                    {status.kind === "loading" ? "Invio..." : "Richiedi Preventivo"}
                    <ArrowRight className="w-4 h-4" />
                </button>
            </div>

            {status.kind === "success" && (
                <div className="flex items-start gap-3 p-4 bg-emerald-50 border border-emerald-300 text-emerald-900" data-testid="quote-success">
                    <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">{status.text}</div>
                </div>
            )}
            {status.kind === "error" && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-300 text-red-900" data-testid="quote-error">
                    <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                    <div className="text-sm">{status.text}</div>
                </div>
            )}

            <style>{`
                .swiss-input {
                    width: 100%;
                    background: transparent;
                    border: 0;
                    border-bottom: 1px solid rgba(10,10,10,0.2);
                    border-radius: 0;
                    padding: 12px 0;
                    font-size: 15px;
                    color: #0a0a0a;
                    outline: none;
                    transition: border-color 0.2s;
                    font-family: "IBM Plex Sans", sans-serif;
                }
                .swiss-input:focus { border-bottom-color: #FF3B30; }
                .swiss-input::placeholder { color: rgba(10,10,10,0.35); }
            `}</style>
        </form>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] font-mono font-bold text-ink/60">
                {label}{required && <span className="text-brand"> *</span>}
            </span>
            <div className="mt-1">{children}</div>
        </label>
    );
}
